// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { isFirebaseConfigured, getFirebaseStatus } from './firebase/config';
import {
  initializeAnalytics,
  trackPageView,
  trackSessionStart,
  setAnalyticsUserId
} from './utils/analytics';

// Import components
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import CreditActivity from './components/activity/CreditActivity';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Blog from './components/blog/Blog';
import BlogPost from './components/blog/BlogPost';

// Component for tracking route changes
const RouteTracker = () => {
  const location = useLocation();
  const { useAuth } = require('./contexts/AuthContext');
  const { currentUser } = useAuth();

  // Track page views on route changes
  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    trackPageView(pageTitle, location.pathname + location.search);
  }, [location]);

  // Set user ID when user logs in
  useEffect(() => {
    if (currentUser) {
      setAnalyticsUserId(currentUser.uid);
    }
  }, [currentUser]);

  return null;
};

// Helper function to get page titles
const getPageTitle = (pathname) => {
  const titles = {
    '/': 'PDF Statement Converter - Home',
    '/login': 'Login - PDF Statement Converter',
    '/register': 'Register - PDF Statement Converter',
    '/forgot-password': 'Reset Password - PDF Statement Converter',
    '/dashboard': 'Dashboard - PDF Statement Converter',
    '/activity': 'Credit Activity - PDF Statement Converter',
    '/blog': 'Blog - PDF Statement Converter',
    '/test': 'Test Page - PDF Statement Converter'
  };
  
  // Handle dynamic blog post routes
  if (pathname.startsWith('/blog/')) {
    return 'Blog Post - PDF Statement Converter';
  }
  
  return titles[pathname] || 'PDF Statement Converter';
};

// Create a separate component that can use useAuth
const AppContent = () => {
  const { useAuth } = require('./contexts/AuthContext');
  const { currentUser, userCredits } = useAuth();

  useEffect(() => {
    console.log('=== AUTH CONTEXT TEST ===');
    console.log('Current User:', currentUser);
    console.log('User Credits:', userCredits);
    console.log('Is Logged In:', !!currentUser);
  }, [currentUser, userCredits]);

  return (
    <div className="min-h-screen bg-gray-50">
      <RouteTracker />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <CreditActivity />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        {/* Test route for debugging */}
        <Route
          path="/test"
          element={
            <div className="container mx-auto p-6">
              <h1 className="text-2xl font-bold mb-6">Day 5 Test</h1>
              <Dashboard />
            </div>
          }
        />
      </Routes>
    </div>
  );
};

function App() {
  // Redirect from Firebase domains to custom domain
  useEffect(() => {
    const hostname = window.location.hostname;
    const isFirebaseHost = hostname.includes('firebaseapp.com') || hostname.includes('.web.app');
    if (isFirebaseHost) {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = 'https://pdfstatementconverter.co.za' + currentPath;
    }
  }, []);

  // Initialize Firebase and Analytics
  useEffect(() => {
    console.log('=== FIREBASE STATUS ===');
    console.log('Configured:', isFirebaseConfigured());
    console.log('Status:', getFirebaseStatus());
    
    // Initialize Analytics
    const analytics = initializeAnalytics();
    if (analytics) {
      console.log('✅ Analytics initialized successfully');
      
      // Track app session start
      trackSessionStart({
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV
      });
    } else {
      console.warn('⚠️ Analytics initialization failed');
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;